export const isNotFoundSpaceError = (error: Error): boolean =>
	error.message?.includes('Cannot find space');

/** Gateway account-type rejections do not describe permission to read the page itself. */
export const isUnsupportedCustomerAccountFieldError = (error: {
	path?: ReadonlyArray<string | number> | null;
	extensions?: Record<string, unknown>;
}): boolean => {
	const { extensions, path } = error;
	if (
		extensions?.errorSource !== 'GRAPHQL_GATEWAY' ||
		extensions.classification !== 'CustomerAccountsNotSupportedException' ||
		String(extensions.statusCode) !== '403'
	) {
		return false;
	}
	return (
		(extensions.service === 'knowledge_discovery' &&
			(!path?.length || path[0] === 'knowledgeDiscovery')) ||
		(extensions.service === 'confluence_analytics' &&
			(!path?.length || path[0] === 'contentAnalyticsViewers'))
	);
};

type ContentGraphQLError = Parameters<typeof isUnsupportedCustomerAccountFieldError>[0] & {
	message?: string;
};

export type ContentPermissionErrorScope =
	| 'optional-permission'
	| 'content-permission'
	| 'sibling-permission'
	| 'unscoped-permission'
	| 'other';

// These root fields have permissions independent of reading the page. Do not include `space`:
// its denial still participates in the existing space/access-link handling.
const OPTIONAL_CONTENT_ROOTS = new Set(['contentAnalyticsViewers', 'knowledgeDiscovery']);

/** Pure permission classification shared by preload and runtime, before either handles errors. */
export function classifyContentPermissionError(
	error: ContentGraphQLError,
	requestedContentId: string | null | undefined,
	resolvedContentId: string | null | undefined,
): ContentPermissionErrorScope {
	const { extensions, path } = error;
	const data = extensions?.data;
	const isPermissionError =
		String(extensions?.statusCode) === '403' ||
		(typeof data === 'object' &&
			data !== null &&
			'authorized' in data &&
			data.authorized === false);
	if (!isPermissionError) {
		return 'other';
	}

	const root = path?.[0];
	if (root === 'content' || root === 'spaceHomepage') {
		return 'content-permission';
	}

	// Never use missing content or the previous page's cache data to dismiss a failure.
	if (
		requestedContentId &&
		requestedContentId === resolvedContentId &&
		((typeof root === 'string' && OPTIONAL_CONTENT_ROOTS.has(root)) ||
			isUnsupportedCustomerAccountFieldError(error))
	) {
		return 'optional-permission';
	}

	// A sibling field's denial does not establish a content denial, even if we do not yet
	// know how its consumer should recover. Retain the error without restricting the route.
	if (typeof root === 'string') {
		return 'sibling-permission';
	}

	// Pathless failures stay conservative; status alone cannot prove their scope.
	return 'unscoped-permission';
}

// The observed gateway IncorrectAuthException has neither a path nor a structured service
// field. Require its exact service-specific message and metadata; status/message alone
// must never establish scope for an otherwise ambiguous permission failure.
const isPathlessAutohighlightsAuthError = (error: ContentGraphQLError): boolean =>
	!error.path?.length &&
	error.extensions?.errorSource === 'GRAPHQL_GATEWAY' &&
	error.extensions.classification === 'IncorrectAuthException' &&
	String(error.extensions.statusCode) === '403' &&
	error.extensions.presentedAuth === 'UNAUTHENTICATED' &&
	error.message === 'Auth category: UNAUTHENTICATED is not allowed in service knowledge_discovery';

/** Only isolate the autohighlights branch after the requested page has resolved. */
export function isContentAutohighlightsError(
	error: ContentGraphQLError,
	requestedContentId: string | null | undefined,
	resolvedContentId: string | null | undefined,
): boolean {
	return Boolean(
		requestedContentId &&
		requestedContentId === resolvedContentId &&
		((error.path?.[0] === 'knowledgeDiscovery' &&
			(error.path.length === 1 || error.path[1] === 'keyPhrases')) ||
			isPathlessAutohighlightsAuthError(error)),
	);
}
